import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';

// import { EnvironmentService } from '../../../services/enviroment.service';
// import { environment } from '../../../services/global-environment';

import { environment } from '../../../services/global-environment';
import { DatabaseService } from '../../../services/database.service';
import * as echarts from 'echarts';
import { FilterResultsPipe } from './filter-results.pipe';

// 風險評估方法介面
interface RiskAssessmentMethod {
  id: string;
  name: string;
  description: string;
  formula: string; // 風險計算公式的描述
  parameters: Array<{
    id: string;
    name: string;
    description: string;
    unit?: string;
    min?: number;
    max?: number;
    options?: Array<{value: string; label: string}>; // 若為選擇題
  }>;
}

// 化學品介面
interface Chemical {
  id: string;
  chineseName: string;
  englishName: string;
  casNumber: string;
  partNumber: string;
  molecularFormula?: string;
  molecularWeight?: number;
  dangerLevel: number; // 1-低風險, 2-中風險, 3-高風險
  hazardClassifications: string[]; // 危害分類
  healthHazards: string[]; // 健康危害
  environmentalHazards: string[]; // 環境危害
  physicalProperties?: {
    boilingPoint?: number; // 沸點(°C)
    meltingPoint?: number; // 熔點(°C)
    flashPoint?: number; // 閃點(°C)
    autoIgnitionTemp?: number; // 自燃溫度(°C)
    vaporPressure?: number; // 蒸氣壓(kPa)
    vaporDensity?: number; // 蒸氣密度
    specificGravity?: number; // 比重
    solubility?: string; // 溶解度
    appearanceColor?: string; // 外觀顏色
    appearanceForm?: string; // 外觀形態(液體/固體/氣體)
    odor?: string; // 氣味
  };
  safetyMeasures?: {
    personalProtection?: string[]; // 個人防護設備
    storageRequirements?: string[]; // 儲存要求
    spillResponse?: string[]; // 洩漏處理
    fireResponse?: string[]; // 火災處理
  };
}

// 部門介面
interface Department {
  id: string;
  name: string;
  location: string;
}

// 風險評估結果介面
interface RiskAssessmentResult {
  id?: string;
  chemicalId: string;
  departmentId: string;
  methodId: string;
  date: string;
  assessedBy: string;
  riskScore: number;
  riskLevel: string; // 高風險/中風險/低風險
  parameters: {[key: string]: any}; // 參數值
  recommendations?: string[];
  notes?: string;
}

@Component({
  selector: 'app-risk-assessment',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule,FilterResultsPipe],
  templateUrl: './risk-assessment.component.html',
  styleUrl: './risk-assessment.component.scss'
})
export class RiskAssessmentComponent implements OnInit {
  // 資料列表
  chemicals: Chemical[] = [];
  departments: Department[] = [];
  assessmentMethods: RiskAssessmentMethod[] = [];
  assessmentResults: RiskAssessmentResult[] = [];

  // 選擇狀態
  selectedChemicalId: string = '';
  selectedDepartmentId: string = '';
  selectedMethodId: string = '';

  // 新評估的參數
  newAssessment: {
    chemicalId: string;
    departmentId: string;
    methodId: string;
    parameters: {[key: string]: any};
    notes: string;
  } = {
    chemicalId: '',
    departmentId: '',
    methodId: '',
    parameters: {},
    notes: ''
  };

  // 視圖狀態
  viewMode: 'chemical' | 'department' | 'method' = 'chemical';
  showAssessmentModal: boolean = false;
  showResultDetailModal: boolean = false;
  loading: boolean = false;
  error: string | null = null;
  chartInstance: echarts.ECharts | null = null;
  selectedResult: RiskAssessmentResult | null = null;

  constructor(
    private http: HttpClient,
    private databaseService: DatabaseService
  ) {}

  ngOnInit(): void {
    this.loadChemicals();
    this.loadDepartments();
    this.loadAssessmentMethods();
    this.loadAssessmentResults();
  }
// 載入化學品資料
loadChemicals(): void {
  this.loading = true;

  // 使用模擬數據而非真實API調用
  setTimeout(() => {
    this.chemicals = [
      {
        id: 'chem-001',
        chineseName: '丙酮',
        englishName: 'Acetone',
        casNumber: '67-64-1',
        partNumber: 'C3H6O',
        molecularFormula: 'C3H6O',
        molecularWeight: 58.08,
        dangerLevel: 2,
        hazardClassifications: ['易燃液體 類別2', '眼睛刺激 類別2'],
        healthHazards: ['吸入可能造成嗜睡或暈眩', '長期暴露可能造成皮膚乾燥或龜裂'],
        environmentalHazards: []
      },
      {
        id: 'chem-002',
        chineseName: '甲醛',
        englishName: 'Formaldehyde',
        casNumber: '50-00-0',
        partNumber: 'CH2O',
        molecularFormula: 'CH2O',
        molecularWeight: 30.03,
        dangerLevel: 3,
        hazardClassifications: ['急毒性物質 類別3', '皮膚腐蝕/刺激 類別1B', '致癌物質 類別1B'],
        healthHazards: ['吸入有毒', '皮膚接觸有毒', '可能導致癌症'],
        environmentalHazards: ['對水生生物有毒']
      },
      {
        id: 'chem-003',
        chineseName: '苯',
        englishName: 'Benzene',
        casNumber: '71-43-2',
        partNumber: 'C6H6',
        molecularFormula: 'C6H6',
        molecularWeight: 78.11,
        dangerLevel: 3,
        hazardClassifications: ['易燃液體 類別2', '致癌物質 類別1A', '生殖細胞致突變性物質 類別1B'],
        healthHazards: ['可能導致遺傳性缺陷', '可能導致癌症', '長期暴露會傷害造血系統'],
        environmentalHazards: ['對水生生物有毒並具有長期影響']
      },
      {
        id: 'chem-004',
        chineseName: '乙醇',
        englishName: 'Ethanol',
        casNumber: '64-17-5',
        partNumber: 'C2H5OH',
        molecularFormula: 'C2H5OH',
        molecularWeight: 46.07,
        dangerLevel: 1,
        hazardClassifications: ['易燃液體 類別2', '眼睛刺激 類別2'],
        healthHazards: ['造成嚴重眼睛刺激'],
        environmentalHazards: []
      },
      {
        id: 'chem-005',
        chineseName: '氫氧化鈉',
        englishName: 'Sodium Hydroxide',
        casNumber: '1310-73-2',
        partNumber: 'NaOH',
        molecularFormula: 'NaOH',
        molecularWeight: 40.00,
        dangerLevel: 2,
        hazardClassifications: ['金屬腐蝕物 類別1', '皮膚腐蝕/刺激 類別1A'],
        healthHazards: ['造成嚴重皮膚灼傷和眼睛損傷'],
        environmentalHazards: ['對水生生物有害']
      }
    ];

    this.loading = false;
  }, 800); // 模擬網路延遲
}

// 載入部門資料
loadDepartments(): void {
  this.loading = true;

  // 使用模擬數據
  setTimeout(() => {
    this.departments = [
      {
        id: 'dept-001',
        name: '實驗研發部',
        location: '3樓實驗室'
      },
      {
        id: 'dept-002',
        name: '生產製造部',
        location: '1樓生產線'
      },
      {
        id: 'dept-003',
        name: '品質管理部',
        location: '2樓QC實驗室'
      },
      {
        id: 'dept-004',
        name: '倉儲運輸部',
        location: '地下一樓倉庫'
      }
    ];

    this.loading = false;
  }, 600);
}

// 載入評估方法
loadAssessmentMethods(): void {
  this.loading = true;

  // 使用模擬數據
  setTimeout(() => {
    this.assessmentMethods = [
      {
        id: 'ccb',
        name: 'CCB方法',
        description: '控制疊加式評估法(Control Banding)，結合危害因子與暴露因子進行風險評估。',
        formula: '風險分數 = 危害因子 * 暴露因子 * 使用量因子 * 控制因子',
        parameters: [
          {
            id: 'hazardFactor',
            name: '危害因子',
            description: '評估化學品自身危害程度，如毒性、致癌性等',
            min: 1,
            max: 10,
            unit: '分'
          },
          {
            id: 'exposureFactor',
            name: '暴露因子',
            description: '評估操作人員暴露於化學品的可能性',
            min: 1,
            max: 10,
            unit: '分'
          },
          {
            id: 'amountFactor',
            name: '使用量因子',
            description: '評估化學品使用量大小',
            min: 1,
            max: 10,
            unit: '分'
          },
          {
            id: 'controlFactor',
            name: '控制因子',
            description: '評估現有防護措施的有效性',
            min: 0.1,
            max: 1,
            unit: '倍率'
          }
        ]
      },
      {
        id: '95percentile',
        name: '95百分位法',
        description: '根據使用條件和暴露情境預測95%可能性下的暴露水平。',
        formula: '風險分數 = (化學品濃度 * 0.3) + (處理時間 * 處理頻率 * 0.2) + (溫度 * 揮發性 * 0.4) / 防護因子',
        parameters: [
          {
            id: 'chemConcentration',
            name: '化學品濃度',
            description: '化學品使用濃度百分比',
            min: 0,
            max: 100,
            unit: '%'
          },
          {
            id: 'handlingDuration',
            name: '每次處理時間',
            description: '每次操作化學品的持續時間',
            min: 0,
            max: 24,
            unit: '小時'
          },
          {
            id: 'handlingFrequency',
            name: '處理頻率',
            description: '每週處理化學品的次數',
            min: 0,
            max: 100,
            unit: '次/週'
          },
          {
            id: 'temperature',
            name: '使用溫度',
            description: '化學品操作環境溫度',
            unit: '°C'
          },
          {
            id: 'volatility',
            name: '揮發性',
            description: '化學品揮發特性',
            min: 1,
            max: 10,
            unit: '分'
          },
          {
            id: 'protectionFactor',
            name: '防護因子',
            description: '個人防護裝備的有效性',
            min: 0.1,
            max: 1,
            unit: '倍率'
          }
        ]
      },
      {
        id: 'create-simple',
        name: 'Create-Simple法',
        description: '歐盟開發的簡易化學品風險評估工具，特別適用於中小企業。',
        formula: '風險分數 = 基於GHS危害分類的基礎分數 * (使用量 * 粉塵程度) / (通風程度 * 控制措施) / 10',
        parameters: [
          {
            id: 'hazardClass',
            name: 'GHS危害分類',
            description: '根據全球化學品統一分類標準的危害等級',
            options: [
              {value: 'A', label: 'A - 最危險 (如致癌物)'},
              {value: 'B', label: 'B - 高危險 (如急毒性物質)'},
              {value: 'C', label: 'C - 中等危險 (如腐蝕性物質)'},
              {value: 'D', label: 'D - 低危險 (如刺激性物質)'},
              {value: 'E', label: 'E - 最小危險 (如無害物質)'}
            ]
          },
          {
            id: 'usageAmount',
            name: '使用量',
            description: '每天使用的化學品數量',
            min: 0,
            unit: '公斤/天'
          },
          {
            id: 'dustiness',
            name: '粉塵產生程度',
            description: '作業過程中產生粉塵或霧氣的可能性',
            min: 1,
            max: 10,
            unit: '分'
          },
          {
            id: 'ventilationLevel',
            name: '通風程度',
            description: '工作區域的通風情況',
            min: 1,
            max: 10,
            unit: '分'
          },
          {
            id: 'controlMeasures',
            name: '控制措施',
            description: '現有的工程控制和管理措施',
            min: 1,
            max: 10,
            unit: '分'
          }
        ]
      }
    ];

    this.loading = false;
  }, 700);
}

// 載入評估結果
loadAssessmentResults(): void {
  this.loading = true;

  // 使用模擬數據
  setTimeout(() => {
    this.assessmentResults = [
      {
        id: 'res-001',
        chemicalId: 'chem-001',
        departmentId: 'dept-001',
        methodId: 'ccb',
        date: '2025-04-15',
        assessedBy: 'Demo User',
        riskScore: 35.6,
        riskLevel: '低風險',
        parameters: {
          'hazardFactor': 6,
          'exposureFactor': 4,
          'amountFactor': 3,
          'controlFactor': 0.5
        },
        notes: '實驗室使用丙酮作為清洗劑，每次使用量小且在通風櫃中操作。'
      },
      {
        id: 'res-002',
        chemicalId: 'chem-002',
        departmentId: 'dept-002',
        methodId: '95percentile',
        date: '2025-04-18',
        assessedBy: 'Demo User',
        riskScore: 78.5,
        riskLevel: '高風險',
        parameters: {
          'chemConcentration': 80,
          'handlingDuration': 6,
          'handlingFrequency': 20,
          'temperature': 35,
          'volatility': 8,
          'protectionFactor': 0.5
        },
        notes: '生產線使用甲醛，需要加強局部排氣裝置並進行定期監測。'
      },
      {
        id: 'res-003',
        chemicalId: 'chem-003',
        departmentId: 'dept-001',
        methodId: 'create-simple',
        date: '2025-04-20',
        assessedBy: 'Demo User',
        riskScore: 82.7,
        riskLevel: '高風險',
        parameters: {
          'hazardClass': 'A',
          'usageAmount': 2.5,
          'dustiness': 6,
          'ventilationLevel': 7,
          'controlMeasures': 5
        },
        notes: '苯用於有機合成實驗，建議查找替代品或加強防護措施。'
      },
      {
        id: 'res-004',
        chemicalId: 'chem-004',
        departmentId: 'dept-003',
        methodId: 'ccb',
        date: '2025-04-22',
        assessedBy: 'Demo User',
        riskScore: 12.0,
        riskLevel: '低風險',
        parameters: {
          'hazardFactor': 3,
          'exposureFactor': 2,
          'amountFactor': 4,
          'controlFactor': 0.5
        },
        notes: '乙醇用於實驗室設備消毒，風險可控。'
      },
      {
        id: 'res-005',
        chemicalId: 'chem-005',
        departmentId: 'dept-002',
        methodId: 'create-simple',
        date: '2025-04-25',
        assessedBy: 'Demo User',
        riskScore: 45.3,
        riskLevel: '中風險',
        parameters: {
          'hazardClass': 'B',
          'usageAmount': 5,
          'dustiness': 3,
          'ventilationLevel': 6,
          'controlMeasures': 8
        },
        notes: '氫氧化鈉用於pH調整，建議加強個人防護裝備。'
      },
      {
        id: 'res-006',
        chemicalId: 'chem-002',
        departmentId: 'dept-003',
        methodId: '95percentile',
        date: '2025-04-28',
        assessedBy: 'Demo User',
        riskScore: 62.4,
        riskLevel: '中風險',
        parameters: {
          'chemConcentration': 40,
          'handlingDuration': 3,
          'handlingFrequency': 10,
          'temperature': 28,
          'volatility': 8,
          'protectionFactor': 0.3
        },
        notes: '品管實驗室使用甲醛進行測試，需要加強個人防護。'
      }
    ];

    // 初始化圖表
    setTimeout(() => {
      this.initRiskChart();
    }, 100);

    this.loading = false;
  }, 900);
}

  // 切換視圖模式
  switchViewMode(mode: 'chemical' | 'department' | 'method'): void {
    this.viewMode = mode;
    setTimeout(() => {
      this.initRiskChart();
    }, 100);
  }

  // 初始化風險圖表
  initRiskChart(): void {
    const chartElement = document.getElementById('riskOverviewChart');
    if (!chartElement) return;

    if (this.chartInstance) {
      this.chartInstance.dispose();
    }
    this.chartInstance = echarts.init(chartElement);
    this.updateRiskChart();
  }

  // 更新風險圖表
  updateRiskChart(): void {
    if (!this.chartInstance) return;

    let data: any[] = [];
    let categories: string[] = [];

    if (this.viewMode === 'chemical') {
      // 依化學品分組
      const groupByChemical = this.groupByProperty(this.assessmentResults, 'chemicalId');
      data = Object.keys(groupByChemical).map(chemId => {
        const chem = this.chemicals.find(c => c.id === chemId);
        const maxRisk = Math.max(...groupByChemical[chemId].map(r => r.riskScore));
        return {
          name: chem ? chem.chineseName : chemId,
          value: maxRisk,
          chemicalId: chemId
        };
      });
      categories = data.map(d => d.name);
    } else if (this.viewMode === 'department') {
      // 依部門分組
      const groupByDept = this.groupByProperty(this.assessmentResults, 'departmentId');
      data = Object.keys(groupByDept).map(deptId => {
        const dept = this.departments.find(d => d.id === deptId);
        const maxRisk = Math.max(...groupByDept[deptId].map(r => r.riskScore));
        return {
          name: dept ? dept.name : deptId,
          value: maxRisk,
          departmentId: deptId
        };
      });
      categories = data.map(d => d.name);
    } else {
      // 依評估方法分組
      const groupByMethod = this.groupByProperty(this.assessmentResults, 'methodId');
      data = Object.keys(groupByMethod).map(methodId => {
        const method = this.assessmentMethods.find(m => m.id === methodId);
        const maxRisk = Math.max(...groupByMethod[methodId].map(r => r.riskScore));
        return {
          name: method ? method.name : methodId,
          value: maxRisk,
          methodId: methodId
        };
      });
      categories = data.map(d => d.name);
    }

    // 依風險分數排序
    data.sort((a, b) => b.value - a.value);
    categories = data.map(d => d.name);

    const option = {
      title: {
        text: this.viewMode === 'chemical' ? '化學品風險評估分布' :
              this.viewMode === 'department' ? '部門風險評估分布' : '評估方法風險分布',
        left: 'center'
      },
      tooltip: {
        trigger: 'axis',
        formatter: '{b}: {c}'
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: categories,
        axisLabel: {
          interval: 0,
          rotate: 45
        }
      },
      yAxis: {
        type: 'value',
        name: '風險分數'
      },
      series: [
        {
          type: 'bar',
          data: data.map(d => ({
            value: d.value,
            itemStyle: {
              color: this.getRiskColor(d.value)
            }
          })),
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          }
        }
      ],
      visualMap: {
        show: false,
        min: 0,
        max: 100,
        inRange: {
          color: ['#91cc75', '#fac858', '#ee6666']
        }
      }
    };

    this.chartInstance.setOption(option);
  }

  // 依屬性分組
  groupByProperty<T>(array: T[], property: keyof T): { [key: string]: T[] } {
    return array.reduce((result, item) => {
      const key = String(item[property]);
      if (!result[key]) {
        result[key] = [];
      }
      result[key].push(item);
      return result;
    }, {} as { [key: string]: T[] });
  }

  // 依風險分數取得顏色
  getRiskColor(score: number): string {
    if (score >= 70) return '#ee6666'; // 高風險紅色
    if (score >= 40) return '#fac858'; // 中風險黃色
    return '#91cc75'; // 低風險綠色
  }

  // 依風險分數取得風險等級
  getRiskLevel(score: number): string {
    if (score >= 70) return '高風險';
    if (score >= 40) return '中風險';
    return '低風險';
  }

  // 打開評估模態框
  openAssessmentModal(chemicalId?: string, departmentId?: string, methodId?: string): void {
    this.newAssessment = {
      chemicalId: chemicalId || '',
      departmentId: departmentId || '',
      methodId: methodId || '',
      parameters: {},
      notes: ''
    };
    this.showAssessmentModal = true;
  }

  // 選擇評估方法時重置參數
  onMethodChange(): void {
    this.newAssessment.parameters = {};
    const method = this.assessmentMethods.find(m => m.id === this.newAssessment.methodId);
    if (method) {
      method.parameters.forEach(param => {
        this.newAssessment.parameters[param.id] = null;
      });
    }
  }

  // 計算風險分數
  calculateRiskScore(): number {
    const method = this.assessmentMethods.find(m => m.id === this.newAssessment.methodId);
    if (!method) return 0;

    // 根據不同評估方法計算風險分數
    switch (method.id) {
      case 'ccb':
        return this.calculateCCBRiskScore();
      case '95percentile':
        return this.calculate95PercentileRiskScore();
      case 'create-simple':
        return this.calculateCreateSimpleRiskScore();
      default:
        return 0;
    }
  }

  // CCB方法風險評估
  calculateCCBRiskScore(): number {
    const params = this.newAssessment.parameters;
    const hazardFactor = Number(params['hazardFactor'] || 0); // 危害因子(1-10)
    const exposureFactor = Number(params['exposureFactor'] || 0); // 暴露因子(1-10)
    const amountFactor = Number(params['amountFactor'] || 0); // 使用量因子(1-10)
    const controlFactor = Number(params['controlFactor'] || 0); // 控制因子(0.1-1)

    // CCB公式: 危害因子 * 暴露因子 * 使用量因子 * 控制因子
    const score = hazardFactor * exposureFactor * amountFactor * controlFactor;
    return Math.min(100, Math.round(score * 10) / 10); // 最高上限100分
  }

  // 95百分位法風險評估
  calculate95PercentileRiskScore(): number {
    const params = this.newAssessment.parameters;
    const chemConcentration = Number(params['chemConcentration'] || 0); // 化學品濃度(%)
    const handlingDuration = Number(params['handlingDuration'] || 0); // 處理時間(小時)
    const handlingFrequency = Number(params['handlingFrequency'] || 0); // 處理頻率(次/週)
    const temperature = Number(params['temperature'] || 0); // 使用溫度(°C)
    const volatility = Number(params['volatility'] || 0); // 揮發性(1-10)
    const protectionFactor = Number(params['protectionFactor'] || 0); // 防護因子(0.1-1)

    // 95百分位公式加權計算
    const baseScore = (chemConcentration * 0.3) + (handlingDuration * handlingFrequency * 0.2) +
                     (temperature * volatility * 0.4) / protectionFactor;
    return Math.min(100, Math.round(baseScore * 10) / 10);
  }

  // Create-Simple方法風險評估
  calculateCreateSimpleRiskScore(): number {
    const params = this.newAssessment.parameters;
    const hazardClass = String(params['hazardClass'] || ''); // GHS危害分類
    const usageAmount = Number(params['usageAmount'] || 0); // 使用量(公斤/天)
    const dustiness = Number(params['dustiness'] || 0); // 粉塵產生程度(1-10)
    const ventilationLevel = Number(params['ventilationLevel'] || 0); // 通風程度(1-10)
    const controlMeasures = Number(params['controlMeasures'] || 0); // 控制措施(1-10)

    // 不同危害等級的基礎分數
    let baseScore = 0;
    switch (hazardClass) {
      case 'A': baseScore = 100; break; // 最危險
      case 'B': baseScore = 75; break;
      case 'C': baseScore = 50; break;
      case 'D': baseScore = 25; break;
      case 'E': baseScore = 10; break; // 最安全
    }

    // Create-Simple計算公式
    const exposureScore = (usageAmount * dustiness) / (ventilationLevel * controlMeasures);
    const finalScore = baseScore * exposureScore / 10;
    return Math.min(100, Math.round(finalScore * 10) / 10);
  }

  // 提交評估
  submitAssessment(): void {
    if (!this.newAssessment.chemicalId || !this.newAssessment.departmentId || !this.newAssessment.methodId) {
      this.error = '請選擇化學品、部門和評估方法';
      return;
    }

    const riskScore = this.calculateRiskScore();
    const newResult: RiskAssessmentResult = {
      chemicalId: this.newAssessment.chemicalId,
      departmentId: this.newAssessment.departmentId,
      methodId: this.newAssessment.methodId,
      date: new Date().toISOString().split('T')[0],
      assessedBy: 'Demo User', // 實際應用中應該從認證系統獲取
      riskScore: riskScore,
      riskLevel: this.getRiskLevel(riskScore),
      parameters: {...this.newAssessment.parameters},
      notes: this.newAssessment.notes
    };

    // this.databaseService.addRiskAssessmentResult(newResult).subscribe(
    //   (result) => {
    //     this.assessmentResults.push(result);
    //     this.showAssessmentModal = false;
    //     this.updateRiskChart();
    //   },
    //   (error) => {
    //     console.error('無法儲存評估結果', error);
    //     this.error = '儲存評估結果失敗';
    //   }
    // );
  }

  // 查看評估結果詳情
  viewAssessmentDetail(result: RiskAssessmentResult): void {
    this.selectedResult = result;
    this.showResultDetailModal = true;
  }

  // 關閉評估詳情模態框
  closeResultDetailModal(): void {
    this.showResultDetailModal = false;
    this.selectedResult = null;
  }

  // 關閉評估模態框
  closeAssessmentModal(): void {
    this.showAssessmentModal = false;
    this.error = null;
  }

  // 獲取化學品名稱
  getChemicalName(chemicalId: string): string {
    const chemical = this.chemicals.find(c => c.id === chemicalId);
    return chemical ? chemical.chineseName : '未知化學品';
  }

  // 獲取部門名稱
  getDepartmentName(departmentId: string): string {
    const department = this.departments.find(d => d.id === departmentId);
    return department ? department.name : '未知部門';
  }

  // 獲取評估方法名稱
  getMethodName(methodId: string): string {
    const method = this.assessmentMethods.find(m => m.id === methodId);
    return method ? method.name : '未知方法';
  }

  // 對化學物質設置建議的防護措施
  generateRecommendations(result: RiskAssessmentResult): string[] {
    const recommendations: string[] = [];

    if (result.riskScore >= 70) {
      // 高風險建議
      recommendations.push('需要進行更嚴格的工程控制，如密閉系統或局部排氣通風');
      recommendations.push('必須使用合適的個人防護設備，包括呼吸防護、化學品防護手套和護目鏡');
      recommendations.push('建議重新評估作業流程，考慮替代化學品或替代工藝');
      recommendations.push('需要定期健康監測和培訓');
    } else if (result.riskScore >= 40) {
      // 中風險建議
      recommendations.push('需要實施工程控制措施，如局部排氣通風');
      recommendations.push('應使用適當的個人防護設備');
      recommendations.push('定期進行暴露監測');
      recommendations.push('考慮工作輪換以減少暴露時間');
    } else {
      // 低風險建議
      recommendations.push('維持良好的通風條件');
      recommendations.push('建議使用基本的個人防護設備');
      recommendations.push('遵循安全操作程序');
    }

    return recommendations;
  }

  // 添加到 RiskAssessmentComponent 類中
getMethodParameters(methodId: string): Array<{id: string, name: string, unit?: string}> {
  const method = this.assessmentMethods.find(m => m.id === methodId);
  return method ? method.parameters : [];
}

}
